import type { ConciseBranch } from '@festina/configuration-general-types/configuration'
import { BranchCategories, STANDARD_BRANCH } from '@festina-life/api'
import type {
  ConfigurableServer,
  Optional,
  SetOptional,
} from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useMemo } from 'react'
import * as Yup from 'yup'

import { useAvailableServersForConfiguration } from '../../system/useAvailableServersForConfiguration'
import { useAllConciseBranches } from '../../uiModels/branches/useAllConciseBranches'

export const useBranchCreationValidationSchema = () => {
  const translate = useTranslate()
  const conciseBranches = useAllConciseBranches()
  const availableServers = useAvailableServersForConfiguration()

  const requiredIfNoBaseBranch = useMemo(
    () => ({
      is: (baseBranch: Optional<ConciseBranch>) => !baseBranch,
      then: (schema: Yup.Schema) =>
        schema.required(translate('validation.required')),
      otherwise: (schema: Yup.Schema) => schema.optional(),
    }),
    [translate],
  )

  return Yup.object({
    branchName: Yup.string()
      .required(translate('validation.required'))
      .noSpaces(translate('validation.noSpaces'))
      .noSeparatorIssues(translate('validation.noSeparatorIssues'))
      .notForbidden(translate('validation.notForbidden'), [STANDARD_BRANCH])
      .test({
        name: 'duplicatedBranchName',
        message: translate(
          'branchCreationForm.validation.branchName.duplicatedName',
        ),
        test: (branchName, context) => {
          const conciseBranchesForServer =
            conciseBranches?.[context.parent.server as ConfigurableServer]

          return !conciseBranchesForServer?.some(
            (specification) => specification.name === branchName,
          )
        },
      }),
    server: Yup.string()
      .oneOf(availableServers)
      .required(translate('validation.required')),
    category: Yup.string()
      .oneOf(BranchCategories)
      .required(translate('validation.required')),
    baseBranch: Yup.object()
      .when('category', {
        is: 'main',
        then: (schema) =>
          schema.nullable().isNull(translate('validation.nullRequired')),
      })
      .when('category', {
        is: 'release',
        then: (schema) =>
          schema.test(
            'mainOrReleaseBranch',
            translate('validation.releaseOnMainOrReleaseBranch'),
            (
              baseConciseBranch: Optional<SetOptional<ConciseBranch, '_type'>>,
            ) =>
              ['release', 'main'].includes(
                baseConciseBranch?.branchCategory ?? '',
              ),
          ),
      })
      .when('category', {
        is: 'feature',
        then: (schema) => schema.required(translate('validation.required')),
      }),
    followsBranch: Yup.object({ name: Yup.string() })
      .nullable()
      .test({
        name: 'differentFromBaseBranch',
        message: translate(
          'branchCreationForm.validation.followsBranch.differentFromBaseBranch',
        ),
        test: (value, context) =>
          !value ||
          !context.parent.baseBranch ||
          value.name !== context.parent.baseBranch.name,
      }),
    typesVersionName: Yup.string()
      .test({
        message: translate('validation.required'),
        name: 'requiredIfOtherFieldsAreDefined',
        test: (value, context) =>
          context.parent.typesVersionDescription ||
          !!context.parent.typeVersionFile?.length
            ? !!value
            : true,
      })
      .when('baseBranch', requiredIfNoBaseBranch),
    typesVersionDescription: Yup.string()
      .when('baseBranch', requiredIfNoBaseBranch)
      .test({
        message: translate('validation.required'),
        name: 'requiredIfOtherFieldsAreDefined',
        test: (value, context) =>
          context.parent.typesVersionName ||
          !!context.parent.typeVersionFile?.length
            ? !!value
            : true,
      }),
    typeVersionFile: Yup.array()
      .when('baseBranch', requiredIfNoBaseBranch)
      .when(['typesVersionName', 'typesVersionDescription'], {
        is: (
          typesVersionName: Optional<string>,
          typesVersionDescription: Optional<string>,
        ) => typesVersionName || typesVersionDescription,
        then: (schema) =>
          schema
            .required(translate('validation.required'))
            .test(
              'file_selected',
              translate('branchTypes.validation.file.empty'),
              (value: File[]) => value?.length === 1,
            ),
      }),
  })
}
